import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  MoreHorizontal, BellOff, Bell, ShieldAlert, Check, Link2, Share2,
} from "lucide-react";
import { toast } from "sonner";
import {
  isDealMuted, muteDeal, unmuteDeal,
  getMuteOverride, setMuteOverride,
} from "../../lib/notify";

/* External-store subscription so React renders synchronously on
   `cf:muted-changed`. Avoids a same-tick race where the pill's
   useEffect listener is still mounting when the toggle fires. */
const subscribeMute = (cb) => {
  window.addEventListener("cf:muted-changed", cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener("cf:muted-changed", cb);
    window.removeEventListener("storage", cb);
  };
};
const useDealMuted = (dealId) =>
  useSyncExternalStore(
    subscribeMute,
    () => (dealId ? (isDealMuted(dealId) ? "1" : "0") : "0"),
    () => "0",
  ) === "1";
const useDealOverride = (dealId) =>
  useSyncExternalStore(
    subscribeMute,
    () => (dealId ? (getMuteOverride(dealId).critical ? "1" : "0") : "0"),
    () => "0",
  ) === "1";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow OS — Deal Room overflow menu
   --------------------------------------------------------------------
   Premium, calm, one-handed control. Inspired by Linear / Apple.

   Provides:
   • Mute / Unmute this transaction
   • Critical alerts override (when muted)
   • Copy room link
   • (Optional) Share via system sheet on mobile

   Visual states:
   • When deal is muted, the trigger button switches to a muted bell
     icon with a soft violet tint and a small "Muted" pill appears
     beside it (rendered by the page header — exposed via prop).
   ─────────────────────────────────────────────────────────────────── */

const usePosition = (anchorRef, open) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !anchorRef.current) return undefined;
    const compute = () => {
      const r = anchorRef.current.getBoundingClientRect();
      const W = 280;
      // Align right edge of menu to right edge of trigger
      const left = Math.max(8, Math.min(window.innerWidth - W - 8, r.right - W));
      const top = Math.min(window.innerHeight - 16, r.bottom + 8);
      setPos({ top, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, anchorRef]);
  return pos;
};

const MenuItem = ({ Icon, label, sub, onClick, danger, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`group w-full flex items-start gap-3 text-left rounded-xl px-3 py-2.5 transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/[0.04]"
    }`}
  >
    <span
      className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
      style={{
        background: danger ? "rgba(244,63,94,0.10)" : "rgba(167,139,250,0.10)",
        border: danger ? "1px solid rgba(244,63,94,0.18)" : "1px solid rgba(167,139,250,0.18)",
        color: danger ? "#FDA4AF" : "#C4B5FD",
      }}
    >
      <Icon size={13.5} strokeWidth={2.1} />
    </span>
    <span className="min-w-0 flex-1">
      <span className={`block text-[12.5px] font-semibold ${danger ? "text-rose-300" : "text-white"} leading-tight`}>{label}</span>
      {sub && <span className="block text-[10.5px] text-slate-400 leading-snug mt-0.5">{sub}</span>}
    </span>
  </button>
);

const ToggleRow = ({ label, sub, checked, onChange, testid, Icon }) => (
  <div
    className="w-full flex items-start gap-3 px-3 py-2.5"
    data-testid={testid}
  >
    <span
      className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
      style={{
        background: "rgba(167,139,250,0.10)",
        border: "1px solid rgba(167,139,250,0.18)",
        color: "#C4B5FD",
      }}
    >
      <Icon size={13.5} strokeWidth={2.1} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="block text-[12.5px] font-semibold text-white leading-tight flex-1 truncate">{label}</span>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          aria-pressed={checked}
          aria-label={`Toggle ${label}`}
          data-testid={`${testid}-switch`}
          className="relative inline-flex shrink-0 transition-colors"
          style={{
            width: 32,
            height: 18,
            borderRadius: 999,
            background: checked
              ? "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)"
              : "rgba(255,255,255,0.10)",
            border: checked ? "1px solid rgba(167,139,250,0.55)" : "1px solid rgba(255,255,255,0.10)",
            boxShadow: checked
              ? "0 6px 14px -4px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.18)"
              : "inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <span
            className="absolute top-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: checked ? 16 : 2,
              width: 12,
              height: 12,
              background: "#fff",
              transition: "left 220ms cubic-bezier(0.16,1,0.3,1), background 200ms ease",
              boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
            }}
          />
        </button>
      </div>
      {sub && <span className="block text-[10.5px] text-slate-400 leading-snug mt-0.5">{sub}</span>}
    </div>
  </div>
);

/* ─── Public component ─────────────────────────────────────────── */
export const DealRoomMuteMenu = ({ deal, variant = "desktop" }) => {
  const dealId = deal?.id;
  const [open, setOpen] = useState(false);
  const muted = useDealMuted(dealId);
  const override = useDealOverride(dealId);
  const anchorRef = useRef(null);
  const pos = usePosition(anchorRef, open);

  // Close on Escape and outside click
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDoc = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      const m = document.getElementById("cf-deal-mute-menu");
      if (m && m.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("touchstart", onDoc);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const toggleMute = () => {
    if (!dealId) return;
    if (muted) {
      unmuteDeal(dealId);
      toast.success("Transaction unmuted", { description: "Push notifications resumed." });
    } else {
      muteDeal(dealId);
      toast("Transaction muted", { description: "Push notifications paused. You'll still see updates here." });
    }
    setOpen(false);
  };

  const toggleOverride = (next) => {
    if (!dealId) return;
    setMuteOverride(dealId, { critical: next });
  };

  const copyLink = () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => toast.success("Deal Room link copied"));
    } else {
      toast(url);
    }
    setOpen(false);
  };

  const shareSheet = () => {
    if (typeof navigator === "undefined") return;
    if (navigator.share) {
      navigator.share({
        title: "ChainFlow Deal Room",
        text: deal?.name || "Deal Room",
        url: window.location.href,
      }).catch(() => null);
    } else {
      copyLink();
    }
    setOpen(false);
  };

  /* ─── Trigger button ───────────────────────────────────────── */
  const isMobile = variant === "mobile";
  const triggerLabel = muted ? "Transaction muted — open menu" : "More options";
  const TriggerIcon = muted ? BellOff : MoreHorizontal;
  const triggerTestid = isMobile ? "dealroom-mobile-more" : "dealroom-header-more";

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
        data-testid={triggerTestid}
        data-muted={muted ? "true" : "false"}
        className={`inline-flex items-center justify-center transition-all active:scale-95 ${
          isMobile
            ? "w-9 h-9 rounded-lg shrink-0"
            : "h-10 w-10 rounded-md shrink-0"
        }`}
        style={{
          background: muted
            ? "linear-gradient(135deg, rgba(139,92,246,0.16), rgba(99,102,241,0.10))"
            : "#FFFFFF",
          border: muted
            ? "1px solid rgba(139,92,246,0.32)"
            : "1px solid rgba(15,23,42,0.10)",
          color: muted ? "#6D28D9" : "#0F172A",
          boxShadow: muted
            ? "0 8px 18px -8px rgba(124,58,237,0.32), inset 0 1px 0 rgba(255,255,255,0.20)"
            : "inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px -2px rgba(15,23,42,0.06)",
        }}
      >
        <TriggerIcon size={isMobile ? 14 : 14.5} strokeWidth={2.1} />
      </button>

      {open && createPortal(
        <>
          {/* mobile-friendly backdrop */}
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[110]"
            style={{ background: "rgba(5,6,16,0.30)", backdropFilter: "blur(2px)" }}
          />

          <div
            id="cf-deal-mute-menu"
            role="menu"
            data-testid="dealroom-mute-menu"
            className="fixed z-[120] cf-fade-up"
            style={{
              top: pos.top,
              left: pos.left,
              width: 280,
            }}
          >
            <div
              className="rounded-2xl overflow-hidden p-1.5"
              style={{
                background:
                  "linear-gradient(160deg, rgba(15,17,38,0.96) 0%, rgba(20,18,52,0.94) 60%, rgba(12,12,30,0.96) 100%)",
                border: "1px solid rgba(139,92,246,0.28)",
                boxShadow:
                  "0 28px 60px -18px rgba(0,0,0,0.65), 0 0 0 1px rgba(139,92,246,0.10), 0 0 32px rgba(124,58,237,0.18)",
                backdropFilter: "blur(18px) saturate(160%)",
                WebkitBackdropFilter: "blur(18px) saturate(160%)",
              }}
            >
              {/* Header label */}
              <div className="px-3 pt-2 pb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300/80">
                  Transaction controls
                </p>
              </div>

              {/* Mute / Unmute */}
              <MenuItem
                Icon={muted ? Bell : BellOff}
                label={muted ? "Unmute this transaction" : "Mute this transaction"}
                sub={
                  muted
                    ? "Resume push notifications and lock-screen alerts."
                    : "Pause push and lock-screen alerts. Updates still sync here."
                }
                onClick={toggleMute}
                testid="dealroom-mute-action"
              />

              {/* Critical override — only relevant once muted */}
              {muted && (
                <ToggleRow
                  Icon={ShieldAlert}
                  label="Critical alerts only"
                  sub="Exchange failure, mortgage expiry, AML or completion risk can still reach you."
                  checked={override}
                  onChange={toggleOverride}
                  testid="dealroom-mute-override"
                />
              )}

              {/* Status hint */}
              {muted && (
                <div className="mx-3 my-2 rounded-lg px-3 py-2 text-[10.5px] text-violet-200/80 inline-flex items-start gap-2"
                  style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.18)" }}>
                  <Check size={12} strokeWidth={2.4} className="mt-0.5 shrink-0" />
                  <span>Muted until re-enabled. You'll still see unread states.</span>
                </div>
              )}

              <div className="my-1 mx-3 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

              <MenuItem
                Icon={Link2}
                label="Copy room link"
                onClick={copyLink}
                testid="dealroom-menu-copy-link"
              />
              <MenuItem
                Icon={Share2}
                label="Share via device"
                sub="Uses your phone's share sheet when available."
                onClick={shareSheet}
                testid="dealroom-menu-share"
              />
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

/* ─── Compact "Muted" pill — for header badge area ───────────── */
export const MutedPill = ({ dealId, className = "" }) => {
  const muted = useDealMuted(dealId);
  if (!muted) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2 h-[22px] rounded-full ${className}`}
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.14), rgba(99,102,241,0.10))",
        color: "#6D28D9",
        border: "1px solid rgba(139,92,246,0.30)",
      }}
      data-testid="dealroom-muted-pill"
    >
      <BellOff size={10} strokeWidth={2.4} /> Muted
    </span>
  );
};

export default DealRoomMuteMenu;
